import React, { useState, useRef, useEffect } from "react";
import { OpenRouterModel, addModelToTextual } from "../../api/openrouterApi";
import { Badge, Button, Icon, Modal } from "../../components/tailwind";
import { showMessage } from "../../utils/partialUtils";
import { NotificationAppearance } from "../../api/djangoToastSlice";

type Props = {
  model: OpenRouterModel;
  token: string | null;
  onModelAdded: () => void;
};

export const ModelListItem: React.FC<Props> = ({
  model,
  token,
  onModelAdded,
}) => {
  const [, setIsModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Sync state when dialog closes (via backdrop click or ESC)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleClose = (): void => {
      setIsModalOpen(false);
    };

    dialog.addEventListener("close", handleClose);
    return () => {
      dialog.removeEventListener("close", handleClose);
    };
  }, []);

  const formatPrice = (price: string): string => {
    // Convert to number if it's a string (DecimalField serializes as string)
    const numPrice = parseFloat(price);
    return `$${numPrice.toFixed(4)}`;
  };

  const handleAddClick = (): void => {
    if (dialogRef.current) {
      dialogRef.current.showModal();
      setIsModalOpen(true);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (!token) return;

    setIsAdding(true);
    try {
      await addModelToTextual(token, model.id);
      showMessage(
        NotificationAppearance.SUCCESS,
        `Successfully added ${model.name} as a GPTModel.`
      );
      if (dialogRef.current) {
        dialogRef.current.close();
      }
      setIsModalOpen(false);
      onModelAdded();
    } catch (error) {
      // Error is handled by APIError class which shows toast automatically
    } finally {
      setIsAdding(false);
    }
  };

  const handleCancel = (): void => {
    if (dialogRef.current) {
      dialogRef.current.close();
    }
    setIsModalOpen(false);
  };

  const supportsImages =
    model.architecture?.input_modalities?.includes("image") || false;

  return (
    <>
      <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm hover:tw-shadow-md tw-transition-shadow tw-p-5 tw-relative">
        {/* Add Button / Already Added */}
        <div className="tw-absolute tw-top-4 tw-right-4">
          {model.is_already_added ? (
            <span className="tw-text-sm tw-text-gray-500 tw-font-medium">
              Already added
            </span>
          ) : (
            <Button
              variant="primary"
              size="sm"
              compact
              icon
              onClick={handleAddClick}
              className="tw-gap-1"
            >
              <Icon name="add" />
              <span>Add</span>
            </Button>
          )}
        </div>

        {/* Header */}
        <div className="tw-mb-4 tw-pr-20">
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-1">
            {model.name}
          </h3>
          <p className="tw-text-sm tw-text-gray-500">{model.id}</p>
        </div>

        {/* Description */}
        <div className="tw-mb-4">
          <p className="tw-text-sm tw-text-gray-700 tw-whitespace-normal">
            {model.description}
          </p>
        </div>

        {/* Details Grid */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-4">
          <div>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-1">Input Pricing</p>
            <p className="tw-text-sm tw-font-medium tw-text-gray-900">
              {formatPrice(model.input_pricing)} /M
            </p>
          </div>
          <div>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
              Output Pricing
            </p>
            <p className="tw-text-sm tw-font-medium tw-text-gray-900">
              {formatPrice(model.output_pricing)} /M
            </p>
          </div>
          <div>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
              Context Length
            </p>
            <p className="tw-text-sm tw-font-medium tw-text-gray-900">
              {model.context_length
                ? model.context_length.toLocaleString()
                : "N/A"}{" "}
              Tokens
            </p>
          </div>

          {model.architecture?.tokenizer && (
            <div>
              <p className="tw-text-xs tw-text-gray-500 tw-mb-1">Tokenizer</p>
              <p className="tw-text-sm tw-font-medium tw-text-gray-900">
                {model.architecture.tokenizer}
              </p>
            </div>
          )}
          {model.architecture?.input_modalities.length > 0 && (
            <div>
              <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
                Input Modalities:
              </p>
              <div className="tw-flex tw-flex-wrap tw-gap-1">
                {model.architecture?.input_modalities.map((modality) => (
                  <Badge key={modality} content={modality} />
                ))}
              </div>
            </div>
          )}
          {model.architecture?.output_modalities.length > 0 && (
            <div>
              <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
                Output Modalities:
              </p>
              <div className="tw-flex tw-flex-wrap tw-gap-1">
                {model.architecture?.output_modalities.map((modality) => (
                  <Badge key={modality} content={modality} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Supported Parameters - Badge Style */}
        {model.supported_parameters?.length > 0 && (
          <div>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-2">
              Supported Parameters
            </p>
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {model.supported_parameters.map((param) => (
                <Badge key={param} content={param} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal dialogRef={dialogRef} size="sm">
        <Modal.Header>
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
            Add Model to Textual
          </h3>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-700">
              Are you sure you want to add <strong>{model.name}</strong> as a
              GPTModel?
            </p>
            <div className="tw-bg-gray-50 tw-rounded-md tw-p-4 tw-space-y-2">
              <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                <div>
                  <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
                    Provider
                  </p>
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900">
                    {model.provider}
                  </p>
                </div>
                <div>
                  <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
                    Model ID
                  </p>
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900">
                    {model.id}
                  </p>
                </div>
                {model.context_length && (
                  <div>
                    <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
                      Context Length
                    </p>
                    <p className="tw-text-sm tw-font-medium tw-text-gray-900">
                      {model.context_length.toLocaleString()} tokens
                    </p>
                  </div>
                )}
                <div>
                  <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
                    Supports Images
                  </p>
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900">
                    {supportsImages ? "Yes" : "No"}
                  </p>
                </div>
              </div>
              {model.description && (
                <div>
                  <p className="tw-text-xs tw-text-gray-500 tw-mb-1">
                    Description
                  </p>
                  <p className="tw-text-sm tw-text-gray-700">
                    {model.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Actions>
          <Button
            size="sm"
            onClick={handleCancel}
            disabled={isAdding}
            data-testid="add-model-cancel"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleConfirm}
            loading={isAdding}
            data-testid="add-model-confirm"
          >
            Yes
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};
